import {
  createContext,
  useReducer,
  ReactNode,
  useContext,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import {
  AppContextValue,
  AppState,
  ChatHistory,
  ChatMessage,
  ChatRequest,
  Conversation,
  FrontendSettings,
  UploadingStatus,
  PromptConfig,
  FAQItem,
  ThemeMode,
  GlossaryItem,
  OrchestrationType,
  getCurrentOrchestration,
} from "../common/models";
import { appStateReducer } from "./AppStateReducer";
import { ChatService, ApiService } from "../services";
import uuid from "react-uuid";
import { useConfig } from "../config/ConfigProvider";
import { AUTHOR_ROLE } from "../pages/chat/utils";
import { useMutation } from "@tanstack/react-query";
import { useIsAuthenticated } from "@azure/msal-react";
import { HttpServiceContext } from "./HttpProvider";
import { AccessContext } from "./AccessProvider";
import { useOAuth } from "../auth/OAuthProvider";
import ChromeExtensionHandler, { ChromeExtensionMessage } from "../utils/chromeExtensionHandler";
import { DeviceUtils } from "../utils/deviceUtils";

type MutationContext = {
  logSuccess: () => void;
  logServerError: (error: string) => void;
  logRetryError: (error: string, failureCount: number) => void;
}

//creating a app context for the app state
const AppStateContext = createContext<AppContextValue | null>(null);

//Create the initial state
const initialState: AppState = {
  isLoading: false,
  isChatHistoryOpen: false,
  frontendSettings: null,
  chatHistory: null,
  isChatHistoryLoading: false,
  currentConversation: { sessionId: null, messages: [], attachments: [] },
  currentOrchState: null,
  currentAgent: null,
  activeDocument: null,
  uploadingStatus: UploadingStatus.STANDBY,
  isPhone: false,
  isChromeExtension: false,
  themeMode: (sessionStorage.getItem("themeMode") as ThemeMode) || "light",
  prompts: [],
  glossary: [],  // Initialize glossary as empty array
  shouldAddContinueOption: false,
  maxStep: 0,
  currentOrchestrationType: OrchestrationType.StartNegotiation,
  isReadOnlyMode: false,
};

interface AppContextProviderProps {
  children: ReactNode;
}

export const AppContextProvider: React.FC<AppContextProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(appStateReducer, initialState);
  const isMsalAuthenticated = useIsAuthenticated();
  const { accessToken } = useContext(AccessContext);

  // Check for Salesforce JWT in localStorage as additional auth method
  const salesforceJwt = localStorage.getItem("app_jwt");
  
  // Get OAuth user state
  const { user: oauthUser } = useOAuth();

  console.log('🎯 AppContextProvider - oauthUser:', oauthUser ? oauthUser.email : 'null', 'accessToken:', !!accessToken, 'salesforceJwt:', !!salesforceJwt);

  // User is authenticated if any of these are true (same logic as CustomAuthenticatedTemplate)
  const isAuthenticated = isMsalAuthenticated || !!accessToken || !!salesforceJwt || !!oauthUser;

  // More specific check for API readiness - we need an actual token for API calls
  // For OAuth, cookies are automatically sent, so user presence means API is ready
  const isApiReady = !!accessToken || !!salesforceJwt || !!oauthUser;
  
  console.log('🎯 AppContextProvider - isAuthenticated:', isAuthenticated, 'isApiReady:', isApiReady);

  const [isApproved, setIsApproved] = useState<boolean | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [isDeveloper, setIsDeveloper] = useState<boolean | null>(null);
  const [isAdvancedUser, setIsAdvancedUser] = useState<boolean | null>(null);
  const [hasPlaygroundAccess, setHasPlaygroundAccess] = useState<boolean | null>(null);
  const [loadingApprovalStatus, setLoadingApprovalStatus] = useState<boolean>(true);
  const [profilePhoto, setProfilePhotoState] = useState<string | null>(() => {
    return sessionStorage.getItem("profilePhoto") || null;
  });
  const [subscriptionVerified, setSubscriptionVerified] = useState<boolean | null>(null);

  const config = useConfig();

  // Memoize the apiService instance
  const { httpService: apiService } = useContext(HttpServiceContext)

  const accessProvider = useContext(AccessContext);

  useEffect(() => {
    // Use OAuth user's profile picture URL if available
    if (oauthUser?.profilePictureUrl) {
      setProfilePhoto(oauthUser.profilePictureUrl);
    } else if (accessProvider.decodedToken) {
      setProfilePhoto(accessProvider.decodedToken?.family_name);
    }
  }, [oauthUser?.profilePictureUrl, accessProvider.decodedToken?.family_name])

  useEffect(() => {
    const abortController = new AbortController();

    // Only fetch if we have a valid access token or OAuth user
    if (accessProvider.accessToken || oauthUser) {
      fetchFAQs(abortController.signal);
      fetchGlossary(abortController.signal);
      fetchPrompts(abortController.signal);
    }

    return () => {
      if (!abortController.signal.aborted) {
        abortController.abort();
      }
    };
  }, [accessProvider.accessToken]);
  useEffect(() => {
    if (profilePhoto) {
      sessionStorage.setItem("profilePhoto", profilePhoto);
    } else {
      sessionStorage.removeItem("profilePhoto");
    }
  }, [profilePhoto]);

  // Chrome Extension Detection and Communication
  useEffect(() => {
    const chromeHandler = ChromeExtensionHandler.getInstance();
    
    // Check if we're in Chrome extension context
    const isChromeExtension = chromeHandler.isChromeExtensionContext();
    
    if (isChromeExtension) {
      console.log('Chrome Extension context detected');
      dispatch({ type: 'SET_IS_CHROME_EXTENSION', payload: true });
      dispatch({ type: 'SET_IS_PHONE', payload: true }); // Force phone mode for extension
    }

    // Listen for Chrome extension messages
    const removeListener = chromeHandler.addListener((message: ChromeExtensionMessage) => {
      console.log('Received Chrome extension message:', message);
      
      switch (message.type) {
        case 'CHROME_EXTENSION_INIT':
          if (message.isChromeExtension) {
            dispatch({ type: 'SET_IS_CHROME_EXTENSION', payload: true });
            dispatch({ type: 'SET_IS_PHONE', payload: true }); // Force phone mode
          }
          break;
          
        case 'PAGE_CONTENT_SCRAPED':
          if (message.data) {
            console.log('Handling scraped content:', message.data);
            // Forward the message to MuiQuestionInput via postMessage
            window.postMessage({
              type: 'PAGE_CONTENT_SCRAPED',
              data: message.data
            }, window.location.origin);
          }
          break;
      }
    });

    return () => {
      removeListener();
    };
  }, []);

  const setProfilePhoto = (photo: string | null) => {
    setProfilePhotoState(photo);
  };

  const chatService = useMemo(() => new ChatService(apiService), [apiService]);

  const [sendMessageAbortController] = useState(new AbortController());

  const onMutate = (fnName: string) => (_request: any): MutationContext => {
    return {
      logSuccess: () => console.log(`Mutation ${fnName} was successful`),
      logServerError: (error: string) => console.log(`Mutation ${fnName} failed due to server error`),
      logRetryError: (error: string, failureCount: number) => console.log(`Mutation ${fnName} failed due to (${failureCount}) retry error`),
    }
  }

  // Set up mutation to handle save of the chat message
  const { isPending: isChatStreaming, mutateAsync: saveChatMessage } = useMutation({
    mutationKey: ['chatMessage'],
    mutationFn: (request: ChatRequest) => {
      if (request.messageId) {
        return chatService.updateStreamingApiEndpoint(request, sendMessageAbortController.signal);
      }

      return chatService.streamingApiEndpoint(request, sendMessageAbortController.signal);
    },
    onMutate: onMutate('saveChatMessage'),
    retry: (_failureCount, error,) => {
      // Retry only if the error is a 500 Internal Server Error
      return error.message.includes('Request failed with status 500');
    },
    retryDelay: 1000,
  })

  useEffect(() => {
    dispatch({ type: 'SET_IS_LOADING', payload: isChatStreaming })
  }, [isChatStreaming])

  ///TODO: Move these methods out of here
  const callChatEndpoint = async (sessionId: string | null, message: ChatMessage, documentId: string | null, update: boolean = false) => {
    const actualSessionId = sessionId || uuid();
    const request: ChatRequest = {
      sessionId: actualSessionId,
      prompt: typeof message.content === "string" ? message.content : "", // <-- ensure string
      documentId: documentId,
      extraLoginInfo: accessProvider.extraLoginInfo,
      orchestrationType: state.currentOrchestrationType
    };
    
    console.log("Extra Login Info being sent to API: ", { request });

    // Update frontend state with session ID if this is a new conversation
    if (!sessionId && state.currentConversation.sessionId !== actualSessionId) {
      // Only set the sessionId to avoid overwriting messages due to dispatch timing
      dispatch({
        type: "SET_SESSION_ID",
        payload: actualSessionId,
      });
    }

    if (update) {
      request.messageId = message.messageId;
    }

    // Add in blank message for assistant to fill in, allows us to have generating state
    const assistantMessageId = uuid();
    dispatch({
      type: "ADD_CHAT_MESSAGE",
      payload: {
        messageId: assistantMessageId,
        content: '',
        authorRole: { label: AUTHOR_ROLE.ASSISTANT },
        timestamp: new Date().toISOString(),
      },
    });

    const onSuccess = async (response: Response, _payload: ChatRequest, context?: MutationContext) => {
      // Response has header, X-Chat-Message-Id, which is the user message id
      // Add user message to chat with this id
      // After streaming is done:
      // ADD THIS:

      const userMessageId = response.headers.get("X-Chat-Message-Id") || uuid();

      dispatch({
        type: "REPLACE_MESSAGE_ID",
        payload: {
          newId: userMessageId,
          oldId: message.messageId, // Placeholder Id to allow UI to visually progress
        },
      });

      const reader = response.body!.getReader();
      const chunks = [];

      let done, value;
      const dec = new TextDecoder();

      try {
        while (!done) {
          ({ value, done } = await reader.read());

          if (done) {
            dispatch({
              type: "ADD_CHAT_MESSAGE",
              payload: {
                messageId: assistantMessageId,
                content: chunks.join(""),
                authorRole: { label: AUTHOR_ROLE.ASSISTANT },
                timestamp: new Date().toISOString(),
              },
            });
            // Use apiService to get the current agent after streaming is done
            try {
              let sessionIdForOrch = actualSessionId;
              if (sessionIdForOrch) {
                const orchState = await apiService.getOrchState(sessionIdForOrch);

                const currentOrchestration = getCurrentOrchestration(orchState);



                if (orchState) {
                  dispatch({ type: "SET_ORCHESTRATION_STATE", payload: orchState });
                  // If you still need currentAgent for legacy code:
                  dispatch({ type: "SET_CURRENT_AGENT", payload: currentOrchestration?.currentAgent! });
                  // distpatch max step update based on orchestration state
                  dispatch({ type: 'SET_MAX_STEP', payload: 0 });

                  if (currentOrchestration?.currentAgent) {
                    dispatch({
                      type: "ADD_CHAT_MESSAGE",
                      payload: {
                        messageId: assistantMessageId,
                        content: chunks.join(""),
                        authorRole: { label: AUTHOR_ROLE.ASSISTANT },
                        timestamp: new Date().toISOString(),
                        agentName: currentOrchestration?.currentAgent, // ✅ Add the agent name!
                      },
                    });
                  }
                }
              }
            } catch (e) {
              console.error("Failed to fetch current agent:", e);
            }
            break;
          }

          // Add new stream chunk to state
          const strval = dec.decode(value, { stream: true });

          chunks.push(strval);

          dispatch({
            type: "ADD_CHAT_MESSAGE",
            payload: {
              messageId: assistantMessageId,
              content: chunks.join(""),
              authorRole: { label: AUTHOR_ROLE.ASSISTANT },
              timestamp: new Date().toISOString(),
            },
          });
        }
      } catch (streamError) {
        console.error("Stream reading error:", streamError);
        // If we got some content before the error, use it
        if (chunks.length > 0) {
          dispatch({
            type: "ADD_CHAT_MESSAGE",
            payload: {
              messageId: assistantMessageId,
              content: chunks.join("") + "\n\n[Response incomplete due to connection error]",
              authorRole: { label: AUTHOR_ROLE.ERROR },
              timestamp: new Date().toISOString(),
            },
          });
        } else {
          // No content received, show error message
          dispatch({
            type: "ADD_CHAT_MESSAGE",
            payload: {
              messageId: assistantMessageId,
              content: "Connection error occurred while receiving response. Please try again.",
              authorRole: { label: AUTHOR_ROLE.ERROR },
              timestamp: new Date().toISOString(),
            },
          });
        }
      }
    }

    saveChatMessage(
      request,
      {
        onSuccess,
        onError: (error) => {
          console.error("Chat message error:", error);
          // Not saved in chat history
          dispatch({
            type: "ADD_CHAT_MESSAGE",
            payload: {
              messageId: assistantMessageId,
              content: "Unable to connect to service onError",
              authorRole: { label: AUTHOR_ROLE.ERROR },
              timestamp: new Date().toISOString(),
            },
          });
        }
      }
    ).catch((error) => {
      console.error("Unhandled chat message error:", error);
      // Add error message to keep conversation context
      dispatch({
        type: "ADD_CHAT_MESSAGE",
        payload: {
          messageId: assistantMessageId,
          content: "Network error occurred. Please try again.",
          authorRole: { label: AUTHOR_ROLE.ERROR },
          timestamp: new Date().toISOString(),
        },
      });
    });

    return sendMessageAbortController;
  }
  // Subscription status is now derived from OAuth user - no separate API call needed
  const refreshSubscriptionStatus = async () => {
    // For OAuth users, subscription status comes from the user object
    if (oauthUser) {
      setSubscriptionVerified(oauthUser.isSubscribed);
    } else {
      setSubscriptionVerified(false);
    }
  };
  const { isPending, mutate: getChatHistory } = useMutation({
    mutationKey: ['chatHistory'],
    mutationFn: async (sessionId: string | null) => {
      const abortController = new AbortController();
      const chatHistory = await chatService.historyApiEndpoint(abortController.signal);

      return chatHistory;
    },
    onMutate: onMutate('getChatHistory'),
    onSuccess: (data) => {
      dispatch({
        type: "SET_CHAT_HISTORY",
        payload: data
      })
    },
    gcTime: 1000 * 60 * 1
  })

  useEffect(() => {
    dispatch({ type: "SET_CHAT_HISTORY_LOADING", payload: isPending });
  }, [isPending])

  const { isPending: isConversationLoading, mutate: getConversationBySessionId } = useMutation({
    mutationKey: ['getConversationBySessionId'],
    mutationFn: async (sessionId: string) => {
      
      const abortController = new AbortController();
      const conversation = await chatService.getConversationBySessionIdApiEndpoint(sessionId, abortController.signal);
      
      return conversation;
    },
    onMutate: onMutate('getConversationBySessionId'),
    onSuccess: (data, sessionId) => {
      

      // Set the specific conversation as current if it has messages
      if (data && data.chatHistory && data.chatHistory[sessionId]) {
        const messages = data.chatHistory[sessionId];
        const attachments = data.attachments?.[sessionId] || [];

        dispatch({
          type: "SET_CURRENT_CONVERSATION",
          payload: {
            sessionId,
            messages,
            attachments
          }
        });

        // After successfully setting the conversation, load the orchestration state
        console.log("Loading orchestration state for session:", sessionId);
        getOrchState(sessionId)
          .then((orchState) => {
            
          })
          .catch((error) => {
            
            // Don't fail the entire operation if orchestration state fails
          });
      } else {
        console.log("No conversation found on backend for session:", sessionId);
        // Don't clear current conversation if it's ongoing - only if we don't have any messages locally either
        if (state.currentConversation.messages.length === 0) {
          dispatch({
            type: "CLEAR_CURRENT_CHAT"
          });
        }
      }
    },
    onError: (error, sessionId) => {
      console.error("Error loading conversation by session ID:", error);
      // Don't clear conversation on error if we have an ongoing conversation
      // Only clear if we don't have any messages locally
      if (state.currentConversation.messages.length === 0) {
        dispatch({
          type: "CLEAR_CURRENT_CHAT"
        });
      }
    },
    gcTime: 1000 * 60 * 1
  })

  //TODO:
  // const getChatHistory = async (sessionId: string) => {
  //   const abortController = new AbortController();

  //   dispatch({ type: "SET_CHAT_HISTORY_LOADING", payload: true });

  //   try {
  //     const chatHistory = await chatService.historyApiEndpoint(
  //       abortController.signal
  //     );

  //     dispatch({
  //       type: "SET_CHAT_HISTORY",
  //       payload: chatHistory,
  //     });
  //     dispatch({ type: "SET_CHAT_HISTORY_LOADING", payload: false });
  //   } catch (e) {
  //     console.error(e);
  //     dispatch({ type: "SET_CHAT_HISTORY_LOADING", payload: false });
  //   }

  //   return abortController;
  // };

  const clearChatHistory = async (sessionId?: string) => {
    const abortController = new AbortController();

    dispatch({ type: "SET_CHAT_HISTORY_LOADING", payload: true });

    try {
      if (sessionId) {
        await chatService.clearSessionHistoryApiEndpoint(sessionId, abortController.signal);

        dispatch({
          type: 'REMOVE_SESSION_FROM_HISTORY',
          payload: sessionId
        })
      } else {
        await chatService.clearHistoryApiEndpoint(abortController.signal);

        dispatch({ type: "CLEAR_CURRENT_CHAT" });
        dispatch({
          type: "SET_CHAT_HISTORY",
          payload: {
            chatHistory: {},
            attachments: {}
          },
        });
      }

      dispatch({ type: "SET_CHAT_HISTORY_LOADING", payload: false });
    } catch (e) {
      console.error(e);
      dispatch({ type: "SET_CHAT_HISTORY_LOADING", payload: false });
    }

    return abortController;
  };

  // Usaed to tell the rest of the app that the user is on a phone or not
  // TODO: Add package to detect user form factor
  // TODO: Create enum of possible breakpoints/ form factors
  useEffect(() => {
    let timeout: any;
    // set up resize listener
    const handleResize = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        const isPhone = window.navigator.userAgent.includes("Mobile");
        dispatch({ type: 'SET_IS_PHONE', payload: isPhone });
      }, 500);
    }

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    }
  }, []);

  const chatHistoryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (chatHistoryTimeoutRef.current) {
        clearTimeout(chatHistoryTimeoutRef.current);
      }
    }
  }, [])

  // Fetch prompts
  const fetchPrompts = async (abortSignal: AbortSignal) => {
    try {
      const prompts = await apiService.getPrompts(abortSignal);
      dispatch({ type: "SET_PROMPTS", payload: prompts });
    } catch (error) {
      console.error("Failed to fetch prompts:", error);
    }
  };

  // Save a new prompt
  const savePrompt = async (newPrompt: PromptConfig, abortSignal: AbortSignal) => {
    try {
      await apiService.savePrompt(newPrompt, abortSignal);
      await fetchPrompts(abortSignal); // Refresh after add
    } catch (error) {
      console.error("Failed to save prompt:", error);
    }
  };
  const updatePrompt = async (existingPrompt: PromptConfig, abortSignal: AbortSignal) => {
    try {
      await apiService.updatePrompt(existingPrompt, abortSignal);
      await fetchPrompts(abortSignal); // Refresh after update
    } catch (error) {
      console.error("Failed to update prompt:", error);
    }
  };
  const deletePrompt = async (prompt: PromptConfig, abortSignal: AbortSignal) => {
    try {
      await apiService.deletePrompt(prompt, abortSignal);
      await fetchPrompts(abortSignal); // Refresh after delete
    } catch (error) {
      console.error("Failed to delete prompt:", error);
    }
  };
  const initiatePrompt = async (prompt: PromptConfig, abortSignal: AbortSignal) => {
    try {
      await apiService.initiatePrompt(prompt, abortSignal);
      await fetchPrompts(abortSignal); // Refresh after initiate
    } catch (error) {
      console.error("Failed to initiate prompt:", error);
    }
  };
  const exportChat = async (exportType: string, sessionId: string, abortSignal: AbortSignal) => {
    try {
      return await apiService.exportChat(exportType, sessionId, abortSignal);
    } catch (error) {
      console.error("Failed to export chat:", error);
      throw error;
    }
  };

  const previewExport = async (exportType: string, sessionId: string, abortSignal: AbortSignal) => {
    try {
      return await apiService.previewExport(exportType, sessionId, abortSignal);
    } catch (error) {
      console.error("Failed to preview export:", error);
      throw error;
    }
  };

  const exportBattleCard = async (sections: Array<{ agentName: string; title: string; content: string }>, sessionId: string, title: string = "Battle Card", abortSignal: AbortSignal, options?: { singlePageRestriction?: boolean; allowTruncation?: boolean }) => {
    try {
      return await apiService.exportBattleCard(sections, sessionId, title, abortSignal, options);
    } catch (error) {
      console.error("Failed to export battle card:", error);
      throw error;
    }
  };
  // For OAuth users, derive all status from user object (roles, subscription)
  // Get OAuth loading state to properly synchronize
  const { loading: oauthLoading } = useOAuth();

  // This eliminates 4 separate API calls (check-approval, is-admin, is-developer, check-subscription)
  useEffect(() => {
    // Keep loading until OAuth loading completes
    if (oauthLoading) {
      console.log('⏳ OAuth still loading, keeping loadingApprovalStatus true');
      return;
    }

    if (oauthUser) {
      console.log('📊 Deriving user status from OAuth user - roles:', oauthUser.roles, 'isSubscribed:', oauthUser.isSubscribed);
      
      // Check if user has "Approved" role or is subscribed
      // The backend auto-adds users to "Approved" group when they subscribe
      const hasApprovedRole = oauthUser.roles?.some((role: string) => 
        role.toLowerCase() === 'approved'
      ) ?? false;
      
      const approved = hasApprovedRole || oauthUser.isSubscribed;
      
      // Check for admin and developer roles
      const admin = oauthUser.roles?.some((role: string) => 
        role.toLowerCase() === 'admin'
      ) ?? false;
      
      const developer = oauthUser.roles?.some((role: string) => 
        role.toLowerCase() === 'developer'
      ) ?? false;

      const advanced = oauthUser.roles?.some((role: string) => 
        role.toLowerCase() === 'advanced'
      ) ?? false;

      // Check for subscription owner role (enterprise plan owner)
      const subscriptionOwner = oauthUser.roles?.some((role: string) =>
        role.toLowerCase() === 'subscription-owner'
      ) ?? false;

      // Playground access should be granted only to explicit `playground` role
      // users. Developers and admins do NOT get playground access by default.
      const playgroundAccess = oauthUser.roles?.some((role: string) =>
        role.toLowerCase() === 'playground'
      ) ?? false;
      
      // Subscription status from user object
      const subscribed = oauthUser.isSubscribed;
      
      console.log('📊 Status results - approved:', approved, 'admin:', admin, 'developer:', developer, 'advanced:', advanced, 'playgroundAccess:', playgroundAccess, 'subscribed:', subscribed);
      
      setIsApproved(approved);
      setIsAdmin(admin);
      setIsDeveloper(developer);
      setIsAdvancedUser(advanced);
      setHasPlaygroundAccess(playgroundAccess);
      setSubscriptionVerified(subscribed);
      setLoadingApprovalStatus(false);
    } else {
      // OAuth loading is complete and no user exists
      console.log('❌ No OAuth user found after loading completed');
      setLoadingApprovalStatus(false);
      setIsApproved(false);
      setIsAdmin(false);
      setIsDeveloper(false);
      setIsAdvancedUser(false);
      setHasPlaygroundAccess(false);
      setSubscriptionVerified(false);
    }
  }, [oauthUser, oauthLoading]);

  // Fallback timeout to prevent infinite loading
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loadingApprovalStatus) {
        console.warn("Approval status loading timeout after 20 seconds, setting to false");
        setLoadingApprovalStatus(false);
        // Only set to false if we're actually authenticated but approval check timed out
        if (isAuthenticated) {
          setIsApproved(false);
        }
      }
    }, 20000); // Increased from 15 to 20 seconds

    return () => clearTimeout(timeout);
  }, [loadingApprovalStatus, isAuthenticated]);
  const refreshApprovalStatus = async () => {
    try {
      const approvalData = await apiService.checkApprovalStatus();
      const adminData = await apiService.checkAdminStatus();
      setIsApproved(approvalData.isApproved);
      setIsAdmin(adminData.isAdmin);
    } catch (e: any) {
      // Handle thrown errors here
      if (
        e.message?.toLowerCase().includes("no accounts found") ||
        e.message?.toLowerCase().includes("not authenticated") ||
        e.status === 401
      ) {
        sessionStorage.clear();
        localStorage.clear();
        window.location.href = "/"; // or your login route
      } else {
        // Optionally handle other errors
        setIsApproved(false);
        setIsAdmin(false);
      }
    }
  };

  const getOrchState = async (sessionId: string) => {
    try {
      
      const orchState = await apiService.getOrchState(sessionId);
      

      if (orchState) {

        const currentOrchestration = getCurrentOrchestration(orchState);

        dispatch({ type: "SET_ORCHESTRATION_STATE", payload: orchState });
        dispatch({ type: "SET_CURRENT_AGENT", payload: currentOrchestration?.currentAgent! });
        
      } else {
        
      }
      return orchState;
    } catch (e) {
      
      return null;
    }
  }

  const [faqs, setFaqs] = useState<FAQItem[]>([]);
  const [glossary, setGlossary] = useState<GlossaryItem[]>([]);

  // FAQ methods
  const fetchFAQs = async (abortSignal: AbortSignal) => {
    try {
      const data = await apiService.getFAQs(abortSignal);
      setFaqs(data);
    } catch (error) {
      console.error("Failed to fetch FAQs:", error);
    }
  };
  const addFAQ = async (faq: FAQItem, abortSignal: AbortSignal) => {
    await apiService.addFAQ(faq, abortSignal);
    await fetchFAQs(abortSignal); // Refresh after add
  };
  const updateFAQ = async (faq: FAQItem, abortSignal: AbortSignal) => {
    await apiService.updateFAQ(faq, abortSignal);
    await fetchFAQs(abortSignal); // Refresh after update
  };
  const deleteFAQ = async (faq: FAQItem, abortSignal: AbortSignal) => {
    await apiService.deleteFAQ(faq, abortSignal);
    await fetchFAQs(abortSignal); // Refresh after delete
  };

  // Glossary methods
  const fetchGlossary = async (abortSignal: AbortSignal) => {
    try {
      const data = await apiService.getGlossary(abortSignal);
      setGlossary(data);
    } catch (error) {
      console.error("Failed to fetch glossary:", error);
    }
  };
  const addGlossary = async (item: GlossaryItem, abortSignal: AbortSignal) => {
    await apiService.addGlossary(item, abortSignal);
    await fetchGlossary(abortSignal); // Refresh after add
  };
  const updateGlossary = async (item: GlossaryItem, abortSignal: AbortSignal) => {
    await apiService.updateGlossary(item, abortSignal);
    await fetchGlossary(abortSignal); // Refresh after update
  };
  const deleteGlossary = async (item: GlossaryItem, abortSignal: AbortSignal) => {
    await apiService.deleteGlossary(item, abortSignal);
    await fetchGlossary(abortSignal); // Refresh after delete
  };

  const setShouldAddContinueOption = (value: boolean) => {
    dispatch({ type: 'SET_SHOULD_ADD_CONTINUE_OPTION', payload: value });
  };

  const setMaxStep = (value: number) => {
    dispatch({ type: 'SET_MAX_STEP', payload: value });
  };

  const setCurrentOrchestrationType = (value: OrchestrationType) => {
    dispatch({ type: 'SET_CURRENT_ORCHESTRATION_TYPE', payload: value });
  };

  const appCtx: AppContextValue = {
    state: {
      ...state,
      isLoading: state.isLoading,
      chatHistory: state.chatHistory,
      currentConversation: state.currentConversation,
      frontendSettings: {
        ...state.frontendSettings,
        ...config,
      } as FrontendSettings,
      isChatHistoryOpen: state.isChatHistoryOpen,
      activeDocument: state.activeDocument,
      uploadingStatus: state.uploadingStatus,
      isPhone: state.isPhone,
      currentAgent: state.currentAgent,
      currentOrchState: state.currentOrchState,
    },
    isApproved,
    isAdmin,
    isDeveloper,
    isAdvancedUser,
    hasPlaygroundAccess,
    setIsApproved,
    setIsAdmin,
    setIsDeveloper,
    setIsAdvancedUser,
    loadingApprovalStatus,
    refreshApprovalStatus,
    sendChatRequest: async (
      sessionId: string | null,
      message: ChatMessage,
      documentId: string | null
    ): Promise<AbortController> => {
      dispatch({ type: "ADD_CHAT_MESSAGE", payload: message });

      return callChatEndpoint(sessionId, message, documentId);
    },
    updateChatMessageRequest: async (
      sessionId: string | null,
      message: ChatMessage,
      documentId: string | null
    ): Promise<AbortController> => {
      dispatch({ type: "UPDATE_CHAT_MESSAGE", payload: message });

      return callChatEndpoint(sessionId, message, documentId, true);
    },
    clearCurrentChat: async (): Promise<AbortController> => {
      const currentSessionId = state.currentConversation.sessionId;
      
      dispatch({ type: "CLEAR_CURRENT_CHAT" });
      
      // Also clear the current session's attachments from chat history
      if (currentSessionId && state.chatHistory) {
        const updatedChatHistory = {
          ...state.chatHistory,
          attachments: {
            ...state.chatHistory.attachments,
            [currentSessionId]: [] // Clear attachments for current session
          }
        };
        
        dispatch({
          type: "SET_CHAT_HISTORY",
          payload: updatedChatHistory
        });
      }

      const abortController = new AbortController();
      return abortController;
    },
    getChatHistory: async (): Promise<void> => {
      if (chatHistoryTimeoutRef.current !== null) {
        clearTimeout(chatHistoryTimeoutRef.current); // Clear the previous timeout
      }

      const newTimeout = setTimeout(() => {
        getChatHistory(state.currentConversation.sessionId);
      }, 1000);

      chatHistoryTimeoutRef.current = newTimeout; // Store the new timeout ID
      return Promise.resolve();
    },
    getConversationBySessionId: async (sessionId: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        getConversationBySessionId(sessionId, {
          onSuccess: () => {
            console.log("getConversationBySessionId wrapper: Successfully loaded conversation");
            resolve();
          },
          onError: (error) => {
            console.error("getConversationBySessionId wrapper: Failed to load conversation", error);
            reject(error);
          }
        });
      });
    },
    getOrchState,
    clearChatHistory: async (): Promise<AbortController> => {
      return clearChatHistory();
    },
    clearSessionHistory: async (sessionId: string): Promise<AbortController> => {
      return clearChatHistory(sessionId);
    },
    stopGenerating: () => { },
    dispatch: dispatch,
    fetchPrompts,
    savePrompt,
    updatePrompt,
    deletePrompt,
    initiatePrompt,
    exportChat,
    previewExport,
    exportBattleCard,
    prompts: state.prompts,
    profilePhoto,
    setProfilePhoto,
    faqs,
    fetchFAQs,
    addFAQ,
    updateFAQ,
    deleteFAQ,
    glossary,
    fetchGlossary,
    addGlossary,
    updateGlossary,
    deleteGlossary,
    subscriptionVerified,
    setSubscriptionVerified,
    refreshSubscriptionStatus,
    setShouldAddContinueOption,
    setMaxStep,
    setCurrentOrchestrationType,
  };


  useEffect(() => {
    const handler = () => {
      const newThemeMode = sessionStorage.getItem("themeMode") || "light";
      const controlled = sessionStorage.getItem("systemControlledTheme");
      // If user has manually set theme, don't override it
      if (controlled === 'true') {
        if (newThemeMode === "dark" || newThemeMode === "light") {
          dispatch({ type: "SET_THEME_MODE", payload: newThemeMode });
        }
      }
    }

    handler(); // Call once to set initial value
    window.addEventListener("storage", handler);

    return () => {
      window.removeEventListener("storage", handler);
    }
  }, []);


  return (
    <AppStateContext.Provider value={appCtx}>
      {children}
    </AppStateContext.Provider>
  );
};

//exporting a custom hook to use the app context
export const useAppStateContext = () => {
  const appCtx = useContext(AppStateContext);

  if (appCtx === null) {
    throw new Error("AppStateContext is null - that should not be the case!");
  }
  return appCtx;
};


